import _defineProperty from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _toConsumableArray from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useState, useCallback, useRef, useEffect, useMemo, createRef } from 'react';
import { useSpacing } from '../../api/spacing/useSpacing';
import { Badge } from '../Badge';
import { StyledAccordion, StyledSectionHeader, StyledSectionPanel, StyledSectionHeaderButton, StyledSectionHeaderLabel, StyledSectionPanelContent, StyledIcon, StyledSectionHeaderButtonContent, StyledIconContainer, StyledSectionHeaderHint, StyledSectionHeaderHintLabel, StyledSublineContainer } from './Accordion.styles';

var noop = function noop() {};

function setPanelHeight(node, value) {
  var defer = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  var action = function action() {
    return node.style.setProperty('--panel-height', value);
  };

  if (defer) {
    window.requestAnimationFrame(action);
  } else {
    action();
  }
}

export var Accordion = React.forwardRef(function Accordion(_ref, ref) {
  var testId = _ref['data-testid'],
      children = _ref.children,
      className = _ref.className,
      id = _ref.id,
      multiple = _ref.multiple,
      restProps = _objectWithoutProperties(_ref, ["data-testid", "children", "className", "id", "multiple"]);

  var accordionSections = useMemo(function () {
    return children ? React.Children.map(children, function (child) {
      return child;
    }) : [];
  }, [children]); // When `multiple` is set this function acts as a mask so that only the first true value is kept.
  // The `stateFn` argument allows this to be applied to both `defaultOpen` and `open`.

  var stateMask = useCallback(function (stateFn) {
    var firstIndex = accordionSections.findIndex(function (child) {
      return stateFn(child.props);
    });
    return accordionSections.map(function (child, index) {
      return stateFn(child.props) && (multiple ? true : index === firstIndex);
    });
  }, [accordionSections, multiple]);
  var defaultState = useMemo(function () {
    return stateMask(function (props) {
      return Boolean(props.defaultOpen);
    });
  }, [stateMask]);

  var _useState = useState(defaultState),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1]; // The `Accordion` will become controlled if `open` is explicitly set (not undefined) on any `AccordionSection`.


  var controlledState = useMemo(function () {
    return accordionSections.some(function (child) {
      return child.props.open !== undefined;
    }) ? stateMask(function (props) {
      return Boolean(props.open);
    }) : null;
  }, [accordionSections, stateMask]);
  useEffect(function () {
    if (controlledState) setState(controlledState);
  }, [controlledState]);
  var sectionToggleRefs = accordionSections.map(function () {
    return createRef();
  });

  var handleKeyDown = function handleKeyDown(refIndex) {
    return function (event) {
      var nextFocusIndex = -1;

      switch (event.key) {
        case 'Home':
          nextFocusIndex = 0;
          break;

        case 'End':
          nextFocusIndex = sectionToggleRefs.length - 1;
          break;

        case 'ArrowUp':
          nextFocusIndex = refIndex === 0 ? sectionToggleRefs.length - 1 : refIndex - 1;
          break;

        case 'ArrowDown':
          nextFocusIndex = refIndex === sectionToggleRefs.length - 1 ? 0 : refIndex + 1;
          break;
      }

      if (nextFocusIndex !== -1) {
        event.preventDefault();
        var nextToggle = sectionToggleRefs[nextFocusIndex].current;
        if (nextToggle) nextToggle.focus();
      }
    };
  };

  var handleToggle = function handleToggle(sectionIndex) {
    var callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
    return function (willOpen) {
      var nextState = multiple ? _toConsumableArray(state) : state.map(function () {
        return false;
      });
      nextState[sectionIndex] = willOpen;
      setState(nextState);
      callback(willOpen);
    };
  }; // This is used to keep track of the section index since all children are mapped over, but only `AccordionSection` children are modified.


  var sectionIndex = 0;
  return React.createElement(StyledAccordion, {
    className: className,
    "data-testid": testId,
    id: id,
    outerSpacing: useSpacing(restProps),
    ref: ref
  }, accordionSections.map(function (child) {
    var modified = React.cloneElement(child, {
      open: controlledState ? controlledState[sectionIndex] : state[sectionIndex],
      defaultOpen: defaultState[sectionIndex],
      onToggle: controlledState ? child.props.onToggle : handleToggle(sectionIndex, child.props.onToggle),
      onKeyDown: handleKeyDown(sectionIndex),
      toggleRef: sectionToggleRefs[sectionIndex]
    });
    sectionIndex++;
    return modified;
  }));
});
export var AccordionSection = function AccordionSection(_ref2) {
  var children = _ref2.children,
      headingLevel = _ref2.headingLevel,
      id = _ref2.id,
      label = _ref2.label,
      subline = _ref2.subline,
      _ref2$defaultOpen = _ref2.defaultOpen,
      defaultOpen = _ref2$defaultOpen === void 0 ? false : _ref2$defaultOpen,
      _ref2$open = _ref2.open,
      open = _ref2$open === void 0 ? defaultOpen : _ref2$open,
      hint = _ref2.hint,
      hintSeverity = _ref2.hintSeverity,
      roleRegion = _ref2.roleRegion,
      icon = _ref2.icon,
      toggleRef = _ref2.toggleRef,
      _ref2$onToggle = _ref2.onToggle,
      onToggle = _ref2$onToggle === void 0 ? noop : _ref2$onToggle,
      _ref2$onKeyDown = _ref2.onKeyDown,
      onKeyDown = _ref2$onKeyDown === void 0 ? noop : _ref2$onKeyDown;
  var panelId = "".concat(id, "__panel");
  var panelRef = useRef(null);
  var contentRef = useRef(null);
  var handleToggle = useCallback(function () {
    return onToggle(!open);
  }, [open, onToggle]);
  useEffect(function () {
    if (contentRef.current && panelRef.current) {
      setPanelHeight(panelRef.current, "".concat(contentRef.current.scrollHeight, "px"));
      window.requestAnimationFrame(function () {
        if (panelRef.current && contentRef.current) {
          setPanelHeight(panelRef.current, open ? "".concat(contentRef.current.scrollHeight, "px") : '0px', true);
        }
      });
    }
  }, [open]);
  var handlePanelTransitionEnd = useCallback(function (event) {
    if (open && panelRef.current && panelRef.current === event.target) {
      setPanelHeight(panelRef.current, 'auto', true);
    }
  }, [open]);

  var styledSectionPanelProps = _objectSpread({
    ref: panelRef,
    open: open,
    defaultOpen: defaultOpen,
    id: panelId,
    onTransitionEnd: handlePanelTransitionEnd
  }, roleRegion && {
    'aria-labelledby': id,
    role: 'region'
  });

  var showHintBadge = hintSeverity && hint;
  var hintLabelProps = {
    id: "".concat(id, "__hint")
  };
  return React.createElement(React.Fragment, null, React.createElement(StyledSectionHeader, {
    as: headingLevel
  }, React.createElement(StyledSectionHeaderButton, {
    id: id,
    "aria-controls": panelId,
    "aria-expanded": open,
    onClick: handleToggle,
    onKeyDown: onKeyDown,
    ref: toggleRef
  }, React.createElement(StyledSectionHeaderButtonContent, null, icon && React.createElement(StyledIconContainer, null, icon), React.createElement(StyledSectionHeaderLabel, null, label), React.createElement(StyledSublineContainer, null, subline), React.createElement(StyledSectionHeaderHint, _objectSpread({}, hintLabelProps), showHintBadge && React.createElement(Badge, {
    variant: hintSeverity,
    spaceInlineEnd: "s"
  }), React.createElement(StyledSectionHeaderHintLabel, null, hint)), React.createElement(StyledIcon, {
    open: open,
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    xmlns: "http://www.w3.org/2000/svg"
  }, React.createElement("path", {
    d: "M7 10L12.5 15.5 18 10",
    stroke: "currentColor",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }))))), React.createElement(StyledSectionPanel, _objectSpread({}, styledSectionPanelProps), React.createElement(StyledSectionPanelContent, {
    ref: contentRef,
    open: open
  }, children)));
};